import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faChevronLeft } from '@fortawesome/free-solid-svg-icons';

const teamMembers = [
    {
        id: 1,
        name: "Alhaji Tanimu Abdullahi",
        role: "CEO & Founder",
        bio: "With over 35 years of industry experience, Alhaji Tanimu Abdullahi is the visionary founder and executive leader of Dari Investment Limited. His deep expertise in the construction sector and an entrepreneurial mindset have enabled him to build a company renowned for delivering excellence across private and public-sector projects. Under his leadership, Dari Investment has repeatedly executed significant works for top-tier clients such as the Petroleum Trust Fund, Federal Ministries, Nigeria Customs Service, Federal Airports Authority of Nigeria, EFCC, TETFund amongst others. He also sits on the boards of several companies, including Microtel Limited, Yasmin Furniture, and Imiran Ventures Limited.",
        avatar: "/ceo_avatar.png",
        fullImage: "https://res.cloudinary.com/dgtbjxhqs/image/upload/c_pad,w_1097,h_1467,ar_3:4,e_improve/v1747834682/ceo_image6_g088p5.png"
    },
    {
        id: 2,
        name: "Niran Adamolekun",
        role: "General Manager, Administration",
        bio: "With about 25 years of managerial experience across the ICT, construction, and tourism sectors, the General Manager brings a multidisciplinary approach to administration. Holding a Master's degree in Administration and a Management Certificate from the University of Roehampton, UK, he is recognized for aligning operational strategies with business growth. His expertise spans business structuring, project leadership, and administrative systems. He has served in executive roles at organizations including Hyperformance Inc., Microtel Limited, e-Tourism Nigeria Ltd., and Eddipat Services and Technology. He currently leads core administrative functions and drives organizational development at Dari.",
        avatar: "https://res.cloudinary.com/dgtbjxhqs/image/upload/v1749494126/A4_Design_6_yzvapg.png",
        fullImage: "https://res.cloudinary.com/dgtbjxhqs/image/upload/v1749493053/A4_Design_2_m8yfst.png"
    },
    {
        id: 3,
        name: "Umar Musa",
        role: "Head of Accounts",
        bio: "Umar Musa is the Head of Accounts and Finance at Dari Investment Limited, bringing over two decades of experience in financial management, corporate accounting. A native of Yola North in Adamawa State, he holds a Diploma in Accounting and has built a solid career across multiple sectors. He began his professional journey as a Cashier at Interstate Flour Mill, later advancing to Accountant at Prime Transit Nigeria Ltd. His leadership capabilities earned him successive managerial positions at Digital Telecommunications Nigeria Ltd and subsequently at Dari Investment Limited, where he now heads the company's account department.",
        avatar: "/account_avatar.png",
        fullImage: "https://res.cloudinary.com/dgtbjxhqs/image/upload/e_improve/v1750013617/account_image_kbhkrn.png"
    },
    {
        id: 4,
        name: "Engr. Akomolafe Damisi",
        role: "Head of Projects",
        bio: "A seasoned and experienced Quantity Surveyor with nearly three decades of field and project management experience, Engr. Damisi leads Dari’s project delivery operations. His background spans a variety of construction sectors, and he is recognized for his deep technical expertise and results-driven approach. He has overseen major national projects such as the renovation of airports and the construction of the TETFund Academic Publishing Centres amongst other turnkey projects. Engr. Damisi is known for combining practical insight with strategic oversight to ensure timely and cost-efficient execution.",
        avatar: "/account_avatar.png",
        fullImage: "https://res.cloudinary.com/dgtbjxhqs/image/upload/c_pad,h_1467/v1750107322/A4_Design_10_pgvoqt.png"
    },
];

const Team = () => {
    const [selectedMember, setSelectedMember] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);

    // Navigation functions for carousel
    const nextSlide = () => {
        setCurrentIndex((prevIndex) => 
            prevIndex === teamMembers.length - 1 ? 0 : prevIndex + 1
        );
    };

    const prevSlide = () => {
        setCurrentIndex((prevIndex) => 
            prevIndex === 0 ? teamMembers.length - 1 : prevIndex - 1
        );
    };

    return (
        <section id="team" className="py-16 px-4 bg-gray-50">
            <div className="max-w-7xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">Our Leadership Team</h2>
                
                {/* Desktop view - team members in a row */}
                <div className="hidden lg:flex flex-row justify-center items-stretch gap-4 mb-12">
                    {teamMembers.map((member) => (
                        <motion.div 
                            key={member.id}
                            className={` cursor-pointer flex flex-col w-56
                                ${selectedMember?.id === member.id ? 'ring-4 ring-blue-500' : ''}`}
                            onClick={() => setSelectedMember(
                                selectedMember?.id === member.id ? null : member
                            )}
                            whileHover={{ y: -5 }}
                            transition={{ duration: 0.3 }}
                        >
                            {/* Name and role */}
                            <h3 className="text-base font-bold text-gray-800 text-center mt-6">{member.name}</h3>
                            <p className="text-sm text-gray-600 text-center mb-1">{member.role}</p>
                            
                            {/* View Profile under the role with FontAwesome icon */}
                            <div className="flex items-center justify-center text-gray-700 mb-2 gap-2">
                                <span className="text-xs mr-3">View Profile</span>
                                <FontAwesomeIcon 
                                    icon={selectedMember?.id === member.id ? faChevronDown : faChevronRight} 
                                    size="xs" 
                                />
                            </div>
                            
                            {/* Image */}
                            <div className="h-32 overflow-hidden flex items-center justify-center">
                                <img 
                                    src={member.fullImage} 
                                    alt={member.name}
                                    className="w-24 h-24 object-cover object-center"
                                />
                            </div>
                            
                            {/* Spacer */}
                            <div className="flex-grow"></div>
                        </motion.div>
                    ))}
                </div>
                
                {/* Mobile & Tablet view - carousel */}
                <div className="lg:hidden relative mb-12">
                    <div className="flex justify-center">
                        <motion.div 
                            className={`cursor-pointer flex flex-col w-64 sm:w-72 md:w-80
                                ${selectedMember?.id === teamMembers[currentIndex].id ? 'ring-4 ring-blue-500' : ''}`}
                            onClick={() => setSelectedMember(
                                selectedMember?.id === teamMembers[currentIndex].id ? null : teamMembers[currentIndex]
                            )}
                            key={`mobile-${teamMembers[currentIndex].id}`}
                            initial={{ opacity: 0.5, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3 }}
                        >
                            {/* Name and role */}
                            <h3 className="text-base font-bold text-gray-800 text-center mt-6">{teamMembers[currentIndex].name}</h3>
                            <p className="text-sm text-gray-600 text-center mb-1">{teamMembers[currentIndex].role}</p>
                            
                            {/* View Profile under the role with FontAwesome icon */}
                            <div className="flex items-center justify-center text-gray-700 mb-2 gap-2">
                                <span className="text-xs mr-3">View Profile</span>
                                <FontAwesomeIcon 
                                    icon={selectedMember?.id === teamMembers[currentIndex].id ? faChevronDown : faChevronRight} 
                                    size="xs" 
                                />
                            </div>
                            
                            {/* Image */}
                            <div className="h-40 overflow-hidden flex items-center justify-center">
                                <img 
                                    src={teamMembers[currentIndex].fullImage} 
                                    alt={teamMembers[currentIndex].name}
                                    className="w-32 h-32 object-cover object-center"
                                />
                            </div>
                            
                            <div className="flex-grow py-4"></div>
                        </motion.div>
                    </div>
                    
                    {/* Enhanced carousel navigation buttons with higher visibility */}
                    <button 
                        className="absolute left-1 sm:-left-6 md:-left-8 top-1/2 transform -translate-y-1/2 bg-white p-3 sm:p-4 rounded-full shadow-2xl z-50 hover:bg-gray-100 active:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 border-2 border-gray-200"
                        onClick={(e) => {
                            e.stopPropagation();
                            prevSlide();
                        }}
                        aria-label="Previous team member"
                    >
                        <FontAwesomeIcon icon={faChevronLeft} className="text-gray-800 text-sm sm:text-base" />
                    </button>
                    
                    <button 
                        className="absolute right-1 sm:-right-6 md:-right-8 top-1/2 transform -translate-y-1/2 bg-white p-3 sm:p-4 rounded-full shadow-2xl z-50 hover:bg-gray-100 active:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 border-2 border-gray-200"
                        onClick={(e) => {
                            e.stopPropagation();
                            nextSlide();
                        }}
                        aria-label="Next team member"
                    >
                        <FontAwesomeIcon icon={faChevronRight} className="text-gray-800 text-sm sm:text-base" />
                    </button>
                    
                    {/* Carousel indicator dots */}
                    <div className="flex justify-center mt-4">
                        {teamMembers.map((member, index) => (
                            <button 
                                key={`dot-${member.id}`}
                                className={`w-2 h-2 mx-1 rounded-full ${index === currentIndex ? 'bg-blue-500' : 'bg-gray-300'}`}
                                onClick={() => setCurrentIndex(index)}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>
                </div>
                
                {/* Bio section that appears below */}
                <AnimatePresence>
                    {selectedMember && (
                        <motion.div 
                            className="bg-white rounded-lg p-6 md:p-8 shadow-lg mb-8"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div>
                                <h3 className="text-xl md:text-2xl font-bold text-gray-800">{selectedMember.name}</h3>
                                <p className="text-md md:text-lg text-gray-600 mb-4">{selectedMember.role}</p>
                                <div className="text-gray-700 leading-relaxed">
                                    {selectedMember.bio}
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </section>
    );
};

export default Team;